import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  searchMasterAssets,
  previewV2BulkTransactions,
  createV2BulkTransactions,
} from '../../composables/fetchAPI';
import { fetchCryptoPrice, getStoredCryptoDetails } from '../../composables/fetchCrypto';
import { getCryptoPriceSymbol } from '../../utils/portfolioType';
import {
  validateCryptoDecimal,
  sanitizeDecimalInput,
  formatCryptoQty,
  formatCryptoPrice,
  formatCryptoUsd,
  shortenDecimalText,
  addDecimal,
  subtractDecimal,
  multiplyDecimal,
  compareDecimal,
  num,
} from '../../utils/cryptoDecimal';
import { showApiErrorToast, getReplayErrorMessages, getReplayRowErrors } from '../../utils/apiErrorToast';
import ManageCashModal from './ManageCashModal';
import ReportIssueButton from '../ReportIssueButton';

// หน้าซื้อขายของพอร์ต CRYPTO — โครงเดียวกับ SellBuyStockModal (เลือกพอร์ต → เลือกเหรียญ → ระบุรายละเอียด → ตรวจสอบและยืนยัน)
// ต่างจากหุ้นตรงที่ตัวเลขทุกช่องเก็บเป็น string และส่งใน decimalValues ตรงตัว
// ตอนยืนยัน ระบบส่งให้หลังบ้านตรวจ (bulk/preview) ก่อน ถ้าผ่านค่อยบันทึกจริงด้วย idempotencyKey ชุดเดิม

type TradeType = 'BUY' | 'SELL';
const MAX_QUEUE = 20;

interface CryptoAsset {
  assetId: string;
  priceSymbol: string;   // X:BTCUSD
  displaySymbol: string; // BTC
  name: string;
}

interface QueuedTrade {
  id: string;
  type: TradeType;
  asset: CryptoAsset;
  quantity: string;
  price: string;
  fee: string;
  tax: string;
  date: string; // YYYY-MM-DD
  subPortId: string;
  subPortName: string;
  isNewAsset: boolean;
  idempotencyKey: string;
}

// ข้อผิดพลาดที่ดักได้ตอนกด "เพิ่มลงคิว" (แสดงใต้ฟอร์มจนกว่าจะแก้ค่า)
interface QueueGuardError {
  message: string;
  shortfall?: string; // เงินสดที่ขาด (ใช้เปิดหน้าฝากเงินพร้อมยอด)
}

interface CryptoTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolios: any[];           // เฉพาะพอร์ต CRYPTO
  initialPortfolioId?: string;
  initialSubPortId?: string;
  initialSymbol?: string;
  isSellAll?: boolean;
  onDone: () => Promise<void> | void;
  // พอร์ตหุ้น: โชว์ใน dropdown ด้วยให้ผู้ใช้เห็นพอร์ตครบ แต่เลือกแล้วจะสลับไปหน้าซื้อขายหุ้น
  stockPortfolios?: any[];
  onSwitchToStock?: (portfolioId: string) => void;
}

const portIdOf = (p: any) => p?.portfolioId || p?.portId || p?.id;
const getTodayStr = () => new Date().toISOString().split('T')[0];
const newKey = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? `crypto-${crypto.randomUUID()}`
    : `crypto-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const holdingToAsset = (h: any): CryptoAsset => ({
  assetId: h.assetId,
  priceSymbol: getCryptoPriceSymbol(h),
  displaySymbol: h.displaySymbol || h.assetSymbol,
  name: h.name || h.assetName || '',
});

// อ่าน error ต่อแถวจากผล preview (หลังบ้านอาจคืน success=false ใน body ทั้งที่ HTTP 200)
function readPreviewErrors(raw: any): { rows: Record<number, string>; general: string[]; ok: boolean } {
  const body = raw?.data ?? raw ?? {};
  const text = (e: any) => e?.th_message || e?.message || e?.code || (typeof e === 'string' ? e : 'ไม่ผ่านการตรวจสอบ');
  const rows: Record<number, string> = {};
  const general: string[] = [];
  (body.items || body.results || []).forEach((row: any, i: number) => {
    const err = row?.error || (Array.isArray(row?.errors) && row.errors[0]);
    if (err || row?.success === false || row?.valid === false) rows[typeof row?.index === 'number' ? row.index : i] = text(err || row);
  });
  (body.errors || raw?.errors || raw?.error?.details?.errors || []).forEach((err: any) => {
    if (typeof err?.index === 'number') rows[err.index] = text(err);
    else general.push(text(err));
  });
  if (raw?.error && !Array.isArray(raw.error) && Object.keys(raw.error).length > 0 && !general.length && !Object.keys(rows).length) {
    general.push(text(raw.error));
  }
  const ok = (body.success ?? raw?.success) !== false && !general.length && !Object.keys(rows).length;
  return { rows, general, ok };
}

const CryptoTradeModal: React.FC<CryptoTradeModalProps> = ({
  isOpen,
  onClose,
  portfolios,
  initialPortfolioId,
  initialSubPortId,
  initialSymbol,
  isSellAll,
  onDone,
  stockPortfolios = [],
  onSwitchToStock,
}) => {
  const [selectedPortId, setSelectedPortId] = useState<string>(initialPortfolioId || portIdOf(portfolios[0]) || '');
  const selectedPortfolio = portfolios.find(p => portIdOf(p) === selectedPortId);
  const subPorts: any[] = useMemo(
    () => (selectedPortfolio?.subPorts || selectedPortfolio?.subPortfolios || []).filter((s: any) => !s.status || s.status === 'ACTIVE'),
    [selectedPortfolio]
  );
  const [selectedSubPortId, setSelectedSubPortId] = useState<string>(initialSubPortId || '');

  const [type, setType] = useState<TradeType>('BUY');
  const [asset, setAsset] = useState<CryptoAsset | null>(null);
  const [symbolInput, setSymbolInput] = useState('');
  const [searchResults, setSearchResults] = useState<CryptoAsset[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const symbolInputRef = useRef<HTMLInputElement | null>(null);

  const [date, setDate] = useState(getTodayStr());
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [fee, setFee] = useState('');
  const [tax, setTax] = useState('');

  const [transactions, setTransactions] = useState<QueuedTrade[]>([]);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isManageCashOpen, setIsManageCashOpen] = useState(false);
  const [holdingSearch, setHoldingSearch] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [guardError, setGuardError] = useState<QueueGuardError | null>(null);
  const [cashTopUp, setCashTopUp] = useState<number | undefined>(undefined);

  const selectedSub = subPorts.find((s: any) => s.subPortId === selectedSubPortId);
  const holdings: any[] = useMemo(
    () => (selectedSub?.holdings || selectedSub?.stocks || []).filter((h: any) => num(h.quantity) > 0),
    [selectedSub]
  );
  const filteredHoldings = holdings.filter((h: any) =>
    String(h.displaySymbol || h.assetSymbol || '').toUpperCase().includes(holdingSearch.trim().toUpperCase())
  );
  const currentHolding = asset ? holdings.find((h: any) => getCryptoPriceSymbol(h) === asset.priceSymbol) : undefined;
  const isNewAssetForm = Boolean(asset && !currentHolding) || Boolean(!asset && symbolInput.trim());

  const cashExact = String(selectedPortfolio?.summary?.cashValue ?? selectedPortfolio?.cashBalances?.[0]?.amount ?? '0');
  const currentCash = num(cashExact);
  const allowNegativeCash = Boolean(selectedPortfolio?.settings?.allowNegativeCash);

  // เงินที่ใช้/ได้จากรายการ: ซื้อ = -(qty×price + fee + tax), ขาย = qty×price - fee - tax
  const cashEffectOf = (t: { type: TradeType; quantity: string; price: string; fee: string; tax: string }) => {
    const gross = multiplyDecimal(t.quantity, t.price);
    const charges = addDecimal(t.fee || '0', t.tax || '0');
    return t.type === 'BUY' ? subtractDecimal('0', addDecimal(gross, charges)) : subtractDecimal(gross, charges);
  };

  // เงินสดคงเหลือแบบตรงตัว หลังหักรายการทั้งหมดในคิว
  const cashAfterQueue = useMemo(
    () => transactions.reduce((cash, t) => addDecimal(cash, cashEffectOf(t)), cashExact),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transactions, cashExact]
  );

  // จำนวนเหรียญที่ยังขายได้ในพอร์ตย่อยนี้ (รวมผลของรายการในคิวที่เป็นเหรียญเดียวกัน)
  const availableQty = (a: CryptoAsset | null, subPortId: string): string => {
    if (!a) return '0';
    const sub = subPorts.find((s: any) => s.subPortId === subPortId);
    const held = (sub?.holdings || sub?.stocks || []).find((h: any) => getCryptoPriceSymbol(h) === a.priceSymbol);
    return transactions
      .filter(t => t.subPortId === subPortId && t.asset.priceSymbol === a.priceSymbol)
      .reduce((qty, t) => (t.type === 'BUY' ? addDecimal(qty, t.quantity) : subtractDecimal(qty, t.quantity)), String(held?.quantity ?? '0'));
  };
  const sellableQty = availableQty(asset, selectedSubPortId);

  // แก้ค่าในฟอร์มเมื่อไหร่ ซ่อนข้อความเตือนเดิม
  useEffect(() => { setGuardError(null); }, [type, asset, quantity, price, fee, tax, selectedSubPortId, date]);


  const summaryTotals = useMemo(() => transactions.reduce((acc, t) => {
    const gross = num(t.quantity) * num(t.price);
    const charges = num(t.fee) + num(t.tax);
    if (t.type === 'BUY') acc.totalBuy += gross + charges;
    else acc.totalSell += gross - charges;
    return acc;
  }, { totalBuy: 0, totalSell: 0 }), [transactions]);

  // เลือกพอร์ตย่อยแรกให้อัตโนมัติ
  useEffect(() => {
    if (!selectedSubPortId || !subPorts.some((s: any) => s.subPortId === selectedSubPortId)) {
      setSelectedSubPortId(subPorts[0]?.subPortId || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPorts]);

  // เปิดมาจากปุ่ม "ขายทั้งหมด" ของตาราง holding
  useEffect(() => {
    if (!initialSymbol || holdings.length === 0) return;
    const h = holdings.find((x: any) =>
      getCryptoPriceSymbol(x) === initialSymbol.toUpperCase() || x.assetSymbol === initialSymbol || x.displaySymbol === initialSymbol
    );
    if (!h) return;
    selectAsset(holdingToAsset(h));
    setType(isSellAll ? 'SELL' : 'BUY');
    if (isSellAll) setQuantity(String(h.quantity));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSymbol, holdings.length]);

  const flash = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);
  };

  // ใช้ราคาทศนิยมตรงตัวจาก gateway เป็นค่าเริ่มต้น (ผู้ใช้แก้เป็นราคาที่ซื้อขายจริงได้)
  const selectAsset = async (a: CryptoAsset) => {
    setAsset(a);
    setSymbolInput(a.displaySymbol);
    setShowDropdown(false);
    setSearchResults([]);
    await fetchCryptoPrice(a.priceSymbol);
    const exact = getStoredCryptoDetails(a.priceSymbol).priceDecimal;
    if (exact) setPrice(exact);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setSymbolInput(value);
    setAsset(null);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setShowDropdown(true);
    setLoadingSearch(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await searchMasterAssets(value.trim(), 'CRYPTO');
        const items: any[] = res?.items || res?.data?.items || res?.data || [];
        setSearchResults(items.filter((it: any) => it.assetId || it.id).map((it: any) => ({
          assetId: it.assetId || it.id,
          priceSymbol: getCryptoPriceSymbol(it),
          displaySymbol: it.displaySymbol || it.baseCurrencySymbol || it.assetSymbol,
          name: it.displayName || it.name || '',
        })));
      } catch (err) {
        console.error('Crypto asset search failed:', err);
        setSearchResults([]);
      } finally {
        setLoadingSearch(false);
      }
    }, 300);
  };

  const handleActionClick = (h: any, action: TradeType) => {
    selectAsset(holdingToAsset(h));
    setType(action);
    setQuantity('');
    flash();
  };

  const handleAddNewAssetClick = () => {
    setAsset(null);
    setSymbolInput('');
    setType('BUY');
    setQuantity('');
    setPrice('');
    flash();
    setTimeout(() => symbolInputRef.current?.focus(), 0);
  };

  const resetForm = () => {
    setQuantity('');
    setFee('');
    setTax('');
  };

  // เหมือน SellBuyStockModal: เพิ่มรายการไม่ผ่าน → แจ้ง toast ด้านบน (และคงแถบเตือนในฟอร์มไว้ เพราะมีปุ่มฝากเงินเพิ่ม)
  const blockAdd = (guard: QueueGuardError) => {
    setGuardError(guard);
    toast.error(guard.message);
  };

  const handleAddTransaction = () => {
    if (transactions.length >= MAX_QUEUE) return toast.error(`เพิ่มได้สูงสุด ${MAX_QUEUE} รายการต่อครั้ง`);
    if (!selectedPortId || !selectedSubPortId) return toast.error('กรุณาเลือกพอร์ตและพอร์ตย่อย');
    if (!asset) return toast.error('กรุณาเลือกเหรียญจากรายการค้นหา');
    if (!asset.assetId) return toast.error('ไม่พบรหัสเหรียญนี้ในระบบ ลองค้นหาเหรียญใหม่อีกครั้ง');

    const checks: Array<[string, string | null]> = [
      ['จำนวนเหรียญ', validateCryptoDecimal(quantity)],
      ['ราคา', validateCryptoDecimal(price)],
      ['ค่าธรรมเนียม', fee ? validateCryptoDecimal(fee, { allowZero: true }) : null],
      ['ภาษี', tax ? validateCryptoDecimal(tax, { allowZero: true }) : null],
    ];
    const failed = checks.find(([, err]) => err);
    if (failed) return toast.error(`${failed[0]}: ${failed[1]}`);

    const qty = quantity.trim();
    const feeStr = fee.trim() || '0';
    const taxStr = tax.trim() || '0';

    if (type === 'SELL') {
      // ขายเกินจำนวนที่ถือ (นับรวมรายการในคิวแล้ว)
      if (compareDecimal(sellableQty, '0') <= 0) {
        blockAdd({ message: `ไม่มี ${asset.displaySymbol} ในพอร์ตย่อยนี้ให้ขาย` });
        return;
      }
      if (compareDecimal(qty, sellableQty) > 0) {
        blockAdd({ message: `ขายเกินจำนวนที่ถือ: ขาย ${formatCryptoQty(qty)} แต่ขายได้อีก ${formatCryptoQty(sellableQty)} ${asset.displaySymbol}` });
        return;
      }
      // ค่าธรรมเนียม+ภาษีมากกว่าเงินที่ได้จากการขาย
      if (compareDecimal(addDecimal(feeStr, taxStr), multiplyDecimal(qty, price.trim())) > 0) {
        blockAdd({ message: 'ค่าธรรมเนียมและภาษีมากกว่าเงินที่ได้จากการขาย กรุณาตรวจสอบตัวเลข' });
        return;
      }
    } else if (!allowNegativeCash) {
      // เงินสดไม่พอซื้อ (นับรวมรายการในคิวแล้ว)
      const cost = addDecimal(multiplyDecimal(qty, price.trim()), addDecimal(feeStr, taxStr));
      if (compareDecimal(cost, cashAfterQueue) > 0) {
        const shortfall = subtractDecimal(cost, compareDecimal(cashAfterQueue, '0') > 0 ? cashAfterQueue : '0');
        blockAdd({
          message: `เงินสดไม่พอ: ต้องใช้ $${formatCryptoPrice(cost)} แต่มีเหลือ $${formatCryptoPrice(cashAfterQueue)}`,
          shortfall,
        });
        return;
      }
    }

    setTransactions(prev => [...prev, {
      id: newKey(),
      type,
      asset,
      quantity: qty,
      price: price.trim(),
      fee: feeStr,
      tax: taxStr,
      date,
      subPortId: selectedSubPortId,
      subPortName: selectedSub?.name || '-',
      isNewAsset: !currentHolding,
      idempotencyKey: newKey(),
    }]);
    setRowErrors({});
    resetForm();
  };

  const handleEditTransaction = (t: QueuedTrade) => {
    setSelectedSubPortId(t.subPortId);
    setType(t.type);
    setAsset(t.asset);
    setSymbolInput(t.asset.displaySymbol);
    setQuantity(t.quantity);
    setPrice(t.price);
    setFee(t.fee === '0' ? '' : t.fee);
    setTax(t.tax === '0' ? '' : t.tax);
    setDate(t.date);
    setTransactions(prev => prev.filter(x => x.id !== t.id));
    setRowErrors({});
    flash();
  };

  const handleRemoveTransaction = (id: string) => {
    setTransactions(prev => prev.filter(x => x.id !== id));
    setRowErrors({});
  };

  const handleSubmitAll = async () => {
    if (!selectedPortId || transactions.length === 0) return;
    const items = transactions.map(t => ({
      type: t.type,
      subPortId: t.subPortId,
      assetId: t.asset.assetId,
      cashCurrency: 'USD',
      transactionDate: new Date(t.date).toISOString(),
      decimalValues: { quantity: t.quantity, price: t.price, fee: t.fee, tax: t.tax },
      idempotencyKey: t.idempotencyKey,
    }));

    setIsSubmitting(true);
    try {
      // 1) ให้หลังบ้านตรวจแบบแม่นยำก่อน (เงินพอไหม ขายเกินไหม) — ยังไม่บันทึก
      let preview;
      let previewBody: any;
      try {
        previewBody = await previewV2BulkTransactions(selectedPortId, items);
        preview = readPreviewErrors(previewBody);
      } catch (err: any) {
        previewBody = err?.response?.data ?? err;
        preview = { ...readPreviewErrors(previewBody), ok: false };
      }
      if (!preview.ok) {
        // Replay Transaction Chain: ใช้ข้อความไทยของแถวที่พัง (th_reason) แทน "Insufficient USD cash."
        const rowMsgs = { ...preview.rows, ...getReplayRowErrors(previewBody) };
        const mapped: Record<string, string> = {};
        Object.entries(rowMsgs).forEach(([idx, msg]) => {
          const t = transactions[Number(idx)];
          if (t) mapped[t.id] = msg;
        });
        setRowErrors(mapped);
        // toast แบบเดียวกับ SellBuyStockModal: ข้อความไทย + Trace ID + ปุ่มคัดลอกแจ้งทีมงาน
        const replayMsgs = getReplayErrorMessages(previewBody);
        showApiErrorToast(
          {
            th_message: replayMsgs[0] || preview.general[0] || 'มีรายการที่ไม่ผ่านการตรวจสอบ กรุณาแก้ไขรายการที่ขึ้นสีแดง',
            details: previewBody?.data ?? previewBody?.error?.details,
            traceId: previewBody?.meta?.traceId,
          },
          'มีรายการที่ไม่ผ่านการตรวจสอบ กรุณาแก้ไขรายการที่ขึ้นสีแดง'
        );
        return;
      }

      // 2) ผ่านแล้วบันทึกจริงด้วย payload และ idempotencyKey ชุดเดิม
      await createV2BulkTransactions(selectedPortId, items);
      toast.success(`ทำรายการสำเร็จ ${transactions.length} รายการ`);
      setTransactions([]);
      await onDone();
      onClose();
    } catch (error: any) {
      showApiErrorToast(error, 'เกิดข้อผิดพลาดในการทำรายการ');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputCls = 'w-full p-2.5 rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]';
  const inputClsMobile = 'w-full p-2.5 rounded-lg border font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]';
  const decimalProps = (value: string, setter: (v: string) => void) => ({
    type: 'text',
    inputMode: 'decimal' as const,
    autoComplete: 'off',
    value,
    placeholder: '0.00',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setter(sanitizeDecimalInput(e.target.value)),
  });

  const formTint = isNewAssetForm
    ? 'bg-purple-500/15 border-purple-500/40'
    : type === 'BUY'
      ? 'bg-[var(--green-text-color-light)]'
      : 'bg-[var(--red-text-color-light)]';

  const symbolDropdown = showDropdown && (
    <ul className="absolute left-0 w-full top-full mt-2 bg-[var(--bg-item)] border border-[var(--border-item-light)] rounded-lg shadow-2xl z-[500] max-h-60 overflow-y-auto">
      {searchResults.map(item => (
        <li
          key={item.assetId}
          className="px-4 py-2 hover:bg-yellow-500/20 cursor-pointer text-[var(--text-color)] flex items-center gap-2"
          onMouseDown={() => { selectAsset(item); flash(); }}
        >
          <span className="font-semibold">{item.displaySymbol}</span>
          <span className="text-gray-400 text-xs truncate">{item.name}</span>
        </li>
      ))}
      {loadingSearch && <li className="px-4 py-2 text-xs text-gray-400">กำลังค้นหา...</li>}
      {!loadingSearch && searchResults.length === 0 && (
        <li className="px-4 py-2 text-xs text-gray-400">ไม่พบเหรียญ "{symbolInput}"</li>
      )}
    </ul>
  );

  const sellAllButton = (mobile: boolean) => type === 'SELL' && compareDecimal(sellableQty, '0') > 0 && (
    <button
      onClick={() => setQuantity(sellableQty)}
      className={mobile
        ? 'mt-1.5 w-full text-[10px] font-black bg-red-500/10 text-red-500 border border-red-500/30 py-1 rounded active:scale-95 break-all'
        : 'w-full text-sm md:w-auto px-6 py-2.5 h-[42px] rounded-lg font-bold shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white border border-red-500/50'}
    >
      ขายทั้งหมด ({formatCryptoQty(sellableQty)})
    </button>
  );

  // ยอดที่ขาดปัดขึ้นเป็นเซ็นต์ เพื่อใช้เป็นค่าเริ่มต้นในหน้าฝากเงิน
  const openTopUp = (shortfall: string) => {
    setCashTopUp(Math.ceil(num(shortfall) * 100) / 100 || 0.01);
    setIsManageCashOpen(true);
  };

  const guardBanner = guardError && (
    <div role="alert" className="col-span-full flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border bg-red-500/10 border-red-500/40 text-red-300 text-xs font-bold">
      <span className="break-words">⚠ {guardError.message}</span>
      {guardError.shortfall && (
        <button
          type="button"
          onClick={() => openTopUp(guardError.shortfall!)}
          className="shrink-0 px-3 py-1.5 rounded bg-green-600 hover:bg-green-500 text-white"
        >
          ฝากเงินเพิ่ม ${formatCryptoUsd(Math.ceil(num(guardError.shortfall) * 100) / 100 || 0.01)}
        </button>
      )}
    </div>
  );

  const portfolioSelect = (cls: string) => (
    <select
      value={selectedPortId}
      onChange={(e) => {
        const next = e.target.value;
        // เลือกพอร์ตหุ้น → สลับไป SellBuyStockModal
        if (onSwitchToStock && stockPortfolios.some(p => portIdOf(p) === next)) {
          onSwitchToStock(next);
          return;
        }
        setSelectedPortId(next);
        setSelectedSubPortId('');
      }}
      disabled={transactions.length > 0}
      title={transactions.length > 0 ? 'ยืนยันหรือลบรายการในคิวก่อนเปลี่ยนพอร์ต' : undefined}
      className={`${cls} disabled:opacity-60`}
    >
      {onSwitchToStock && stockPortfolios.length > 0 ? (
        <>
          <optgroup style={{ color: 'var(--yellow-text-color)', fontWeight: 800 }} label="พอร์ตคริปโต">
            {portfolios.map(p => <option style={{ color: 'var(--text-color)', fontWeight: 600 }} key={portIdOf(p)} value={portIdOf(p)}>{p.name}</option>)}
          </optgroup>
          <optgroup style={{ color: 'var(--yellow-text-color)', fontWeight: 800 }} label="พอร์ตหุ้น (เปิดหน้าซื้อขายหุ้น)">
            {stockPortfolios.map(p => <option style={{ color: 'var(--text-color)', fontWeight: 600 }} key={portIdOf(p)} value={portIdOf(p)}>{p.name}</option>)}
          </optgroup>
        </>
      ) : (
        portfolios.map(p => <option key={portIdOf(p)} value={portIdOf(p)}>{p.name}</option>)
      )}
    </select>
  );

  const subPortSelect = (cls: string) => (
    <select value={selectedSubPortId} onChange={(e) => setSelectedSubPortId(e.target.value)} className={cls}>
      {subPorts.map((sub: any) => <option key={sub.subPortId} value={sub.subPortId}>{sub.name}</option>)}
    </select>
  );

  const queueRowNet = (t: QueuedTrade) => {
    const gross = num(t.quantity) * num(t.price);
    const charges = num(t.fee) + num(t.tax);
    return { gross, net: t.type === 'BUY' ? gross + charges : gross - charges };
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-4 bg-black/80 md:bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="w-full h-full md:h-[95vh] md:max-w-7xl bg-[var(--bg-chart)] md:rounded-2xl shadow-2xl overflow-hidden text-[var(--text-color)] md:border border-[var(--border-item-light)] relative flex flex-col">

        {isManageCashOpen && selectedPortfolio && (
          <ManageCashModal
            isOpen={isManageCashOpen}
            onClose={() => setIsManageCashOpen(false)}
            onSave={() => { setIsManageCashOpen(false); setGuardError(null); onDone(); }}
            portfolioId={selectedPortId}
            currentCash={currentCash}
            isCrypto
            currentCashExact={cashExact}
            initialAmount={cashTopUp}
          />
        )}

        {isSubmitting && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 animate-fadeIn">
            <div className="w-full max-w-sm bg-[var(--bg-chart)] border border-[var(--border-item-light)] rounded-2xl shadow-2xl p-6 flex flex-col items-center gap-4 text-[var(--text-color)]">
              <div className="w-12 h-12 rounded-full border-4 border-[var(--bg-item)] border-t-[var(--button-edit-bg)] animate-spin" />
              <div className="text-center">
                <h3 className="text-lg font-black">กำลังตรวจสอบและบันทึกรายการ</h3>
                <p className="mt-1 text-xs text-gray-400">
                  ส่งคำสั่งทั้งหมด <span className="font-bold text-[var(--yellow-text-color)]">{transactions.length}</span> รายการ กรุณาอย่าปิดหน้าต่างนี้
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ============================== 💻 DESKTOP VIEW ============================== */}
        <div className="hidden md:flex flex-row w-full h-full">

          {/* ----- ฝั่งซ้าย Desktop ----- */}
          <div className="w-1/3 flex flex-col border-r h-full" style={{ borderColor: 'var(--border-item-light)', backgroundColor: 'var(--component-bg-color)' }}>
            <div className="p-5 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="bg-yellow-400 text-black w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span> เลือกพอร์ต & พอร์ตย่อย
              </h2>
              <div className="space-y-4">
                <div>
                  {portfolioSelect('w-full p-2.5 rounded-lg border focus:outline-none font-bold bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)]')}
                  {selectedPortId && (
                    <div className="mt-2 p-2 rounded-lg flex flex-wrap items-center justify-between border shadow-sm gap-2 bg-[var(--component-bg-color)] border-[var(--border-item-light)]">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[var(--secondary-text-color)]">เงินสด:</span>
                        <span className="text-sm font-bold text-green-500" title={formatCryptoPrice(cashExact)}>${shortenDecimalText(formatCryptoPrice(cashExact))}</span>
                      </div>
                      <button onClick={() => { setCashTopUp(undefined); setIsManageCashOpen(true); }} className="text-[10px] font-bold px-2 py-1 rounded transition-colors uppercase bg-[var(--button-edit-bg)] text-[var(--text-black)]">ปรับยอด</button>
                    </div>
                  )}
                  {transactions.length > 0 && (
                    <div className="flex items-center justify-between border-t pt-1.5 mt-1 border-[var(--border-item-light)] text-xs">
                      <span className="font-semibold text-gray-400">คงเหลือ (โดยคร่าว):</span>
                      <span className={`font-bold ${compareDecimal(cashAfterQueue, '0') < 0 ? 'text-red-400' : 'text-yellow-400'}`}>${formatCryptoPrice(cashAfterQueue)}</span>
                    </div>
                  )}
                </div>
                {subPorts.length > 0 && (
                  <div>{subPortSelect('w-full p-2.5 rounded-lg border focus:outline-none font-bold bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)]')}</div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <span className="bg-yellow-400 text-black w-5 h-5 rounded-full flex items-center justify-center text-xs">2</span> เลือกเหรียญที่มีอยู่
                </h3>
                <button
                  onClick={handleAddNewAssetClick}
                  className="text-sm md:text-base font-bold px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500 hover:text-white transition-colors border border-purple-500/40 flex items-center gap-1.5 shadow-sm"
                >
                  + เพิ่มเหรียญใหม่
                </button>
              </div>
              <input
                type="text"
                placeholder="ค้นหาเหรียญ..."
                value={holdingSearch}
                onChange={(e) => setHoldingSearch(e.target.value)}
                className="w-full mb-3 p-2 text-sm rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]"
              />
              {filteredHoldings.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {filteredHoldings.map((h: any) => (
                    <div key={h.assetId || h.stockSymbol} className="p-3 rounded-lg shadow-sm border bg-[var(--bg-item)] border-[var(--border-item-light)]">
                      <div className="flex justify-between items-start gap-3 mb-2">
                        <span className="font-black text-lg text-[var(--tab-selected)]">{h.displaySymbol || h.assetSymbol}</span>
                        <div className="flex flex-col items-end min-w-0">
                          <span className="text-base font-medium opacity-80 text-right whitespace-nowrap" title={formatCryptoQty(h.quantity)}>@ {shortenDecimalText(formatCryptoQty(h.quantity))} เหรียญ</span>
                          <span className="text-sm font-semibold whitespace-nowrap" style={{ color: 'var(--yellow-text-color)' }} title={formatCryptoPrice(h.avgEntryPrice ?? h.avgBuyPrice ?? '0')}>
                            ราคาเฉลี่ย: ${shortenDecimalText(formatCryptoPrice(h.avgEntryPrice ?? h.avgBuyPrice ?? '0'))}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleActionClick(h, 'BUY')} className="flex-1 bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-white font-bold py-1.5 rounded transition-colors">ซื้อเพิ่ม</button>
                        <button onClick={() => handleActionClick(h, 'SELL')} className="flex-1 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white font-bold py-1.5 rounded transition-colors">ขายออก</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-4 opacity-50 text-sm border border-dashed rounded-lg border-[var(--border-item-light)]">
                  ยังไม่มีเหรียญในพอร์ตย่อยนี้<br />(ค้นหาเหรียญที่ช่องด้านขวาได้เลย)
                </div>
              )}
            </div>
          </div>

          {/* ----- ฝั่งขวา Desktop ----- */}
          <div className="w-2/3 flex flex-col h-full">
            <div className="p-5 border-b flex justify-between items-center bg-black/10 border-[var(--border-item-light)]">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <span className="bg-yellow-400 text-black w-8 h-8 rounded-full flex items-center justify-center text-lg">3</span> ระบุรายละเอียด
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-500 transition-colors" aria-label="ปิด">✕</button>
            </div>

            <div className="p-5 flex-1 overflow-y-auto">
              <div className={`grid grid-cols-5 gap-3 mb-6 p-4 rounded-xl border shadow-inner transition-all duration-300 border-[var(--border-item-light)] ${formTint} ${isAnimating ? 'ring-2 ring-yellow-400 scale-[1.01]' : ''}`}>
                <div className="col-span-1">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">วันที่ (Date) <span className="text-red-500">*</span></label>
                  <input type="date" value={date} max={getTodayStr()} onChange={(e) => setDate(e.target.value)} className={inputCls} />
                </div>
                <div className="col-span-1">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">ประเภทรายการ <span className="text-red-500">*</span></label>
                  <select value={type} onChange={(e) => setType(e.target.value as TradeType)} className={`${inputCls} font-black`}>
                    <option value="BUY">BUY (ซื้อ)</option>
                    <option value="SELL">SELL (ขาย)</option>
                  </select>
                </div>
                <div className="col-span-1 relative">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">เหรียญ <span className="text-red-500">*</span></label>
                  <input
                    ref={symbolInputRef}
                    type="text"
                    className={`${inputCls} uppercase font-black ${asset ? '' : symbolInput ? 'border-yellow-400' : ''}`}
                    placeholder="เช่น BTC, ETH"
                    value={symbolInput}
                    onChange={handleSearchChange}
                    onFocus={() => { if (symbolInput && !asset) setShowDropdown(true); }}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    autoComplete="off"
                  />
                  {symbolDropdown}
                </div>
                <div className="col-span-1">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">จำนวนเหรียญ (Qty) <span className="text-red-500">*</span></label>
                  <input {...decimalProps(quantity, setQuantity)} className={inputCls} />
                </div>
                <div className="col-span-1">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">ราคา USD (Price) <span className="text-red-500">*</span></label>
                  <input {...decimalProps(price, setPrice)} className={inputCls} />
                </div>

                <div className="col-span-1 mt-1">
                  <label className="block text-xs font-bold mb-1.5 opacity-80">ค่าธรรมเนียม (Fee)</label>
                  <input {...decimalProps(fee, setFee)} className={inputCls} />
                </div>
                <div className="col-span-1 mt-1">
                  <label className="block text-xs font-bold mb-1.5 opacity-80">ภาษี (Tax)</label>
                  <input {...decimalProps(tax, setTax)} className={inputCls} />
                </div>
                <div className="col-span-3 flex justify-end items-end gap-2 mt-1">
                  {sellAllButton(false)}
                  <button onClick={handleAddTransaction} className="w-full text-sm md:w-auto px-8 py-2.5 h-[42px] rounded-lg font-bold shadow-md transition-transform active:scale-95 flex items-center justify-center gap-2 bg-[#007aff] hover:bg-blue-600 text-white">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                    เพิ่มลงคิวทำรายการ
                  </button>
                </div>
                {guardBanner}
              </div>

              <div className="flex items-center mb-3">
                <h3 className="font-bold text-base flex items-center gap-2">
                  <span className="bg-yellow-400 text-black w-5 h-5 rounded-full flex items-center justify-center text-xs">4</span> ตรวจสอบและยืนยัน
                </h3>
                <span className={`text-xs ml-2 font-bold px-2 py-0.5 rounded ${transactions.length >= MAX_QUEUE ? 'bg-red-500/20 text-red-500' : 'bg-gray-800 text-gray-300'}`}>
                  ({transactions.length}/{MAX_QUEUE} รายการ)
                </span>
              </div>
              <div className="overflow-x-auto rounded-lg border shadow-sm border-[var(--border-item-light)]">
                <table className="w-full text-sm text-left">
                  <thead className="font-bold bg-[var(--bg-item)] whitespace-nowrap">
                    <tr>
                      <th className="px-4 py-3">ประเภท</th>
                      <th className="px-4 py-3">เหรียญ</th>
                      <th className="px-4 py-3">วันที่</th>
                      <th className="px-4 py-3 text-right">รายละเอียด</th>
                      <th className="px-4 py-3 text-right">ค่าธรรมเนียม/ภาษี</th>
                      <th className="px-4 py-3 text-right text-[var(--yellow-text-color)]">ภาพรวม</th>
                      <th className="px-4 py-3 text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 opacity-50 font-medium">ยังไม่มีรายการ</td></tr>
                    ) : transactions.map(t => {
                      const { gross, net } = queueRowNet(t);
                      const err = rowErrors[t.id];
                      return (
                        <React.Fragment key={t.id}>
                          <tr className={`border-t transition-colors border-[var(--border-item-light)] ${err ? 'bg-red-500/10' : t.isNewAsset ? 'bg-purple-500/15' : ''}`}>
                            <td className="px-4 py-3 font-black" style={{ color: t.type === 'BUY' ? 'var(--green-btn)' : 'var(--red-text-color)' }}>{t.type}</td>
                            <td className="px-4 py-3 font-bold">
                              <div className="relative inline-block pr-3">
                                {t.asset.displaySymbol}
                                {t.isNewAsset && <span className="absolute -top-3 -right-3 bg-yellow-400 text-black text-[9px] font-black p-1 rounded shadow-sm scale-90 leading-none">NEW</span>}
                              </div>
                              <span className="text-[11px] font-normal text-yellow-400/80 truncate max-w-[150px] block">
                                {selectedPortfolio?.name} / {t.subPortName}
                              </span>
                            </td>
                            <td className="px-4 py-3 opacity-80 text-xs whitespace-nowrap">{new Date(t.date).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="font-bold break-all">{formatCryptoQty(t.quantity)} เหรียญ</div>
                              <div className="text-xs opacity-70">@ ${formatCryptoPrice(t.price)} (=${formatCryptoUsd(gross)})</div>
                            </td>
                            <td className="px-4 py-3 text-right text-xs opacity-80 whitespace-nowrap">
                              <div>F: ${formatCryptoPrice(t.fee)}</div>
                              <div>T: ${formatCryptoPrice(t.tax)}</div>
                            </td>
                            <td className="px-4 py-3 text-right font-black text-[var(--yellow-text-color)] whitespace-nowrap">${formatCryptoUsd(net)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1.5">
                                <button onClick={() => handleEditTransaction(t)} className="bg-yellow-500/10 text-yellow-500 font-bold px-3 py-1.5 rounded hover:bg-yellow-500 hover:text-black transition-colors">แก้ไข</button>
                                <button onClick={() => handleRemoveTransaction(t.id)} className="bg-red-500/10 text-red-500 font-bold px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">ลบ</button>
                              </div>
                            </td>
                          </tr>
                          {err && (
                            <tr className="bg-red-500/10">
                              <td colSpan={7} className="px-4 pb-3 text-xs font-bold text-red-400">⚠ {err}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {transactions.length > 0 && (
                <div className="mt-4 p-4 rounded-xl border flex flex-col md:flex-row justify-between items-center gap-3 bg-[var(--bg-item)] border-[var(--border-item-light)] text-xs md:text-sm font-bold">
                  <div className="flex items-center gap-6">
                    <span className="text-green-400">รายการซื้อทั้งหมด: ${formatCryptoUsd(summaryTotals.totalBuy)}</span>
                    <span className="text-red-400">รายการขายทั้งหมด: ${formatCryptoUsd(summaryTotals.totalSell)}</span>
                  </div>
                  <div className="text-gray-400 font-normal text-xs">ยอดโดยประมาณ ระบบจะตรวจยอดจริงอีกครั้งตอนกดยืนยัน</div>
                </div>
              )}
            </div>

            <div className="p-5 border-t flex items-center justify-between bg-black/10 border-[var(--border-item-light)]">
              <div className="w-fit min-w-[140px]">
                <ReportIssueButton
                  jobId="CRYPTO_TRADE_MODAL"
                  errorMessage="พบปัญหาในหน้าต่าง ซื้อ-ขาย เหรียญ"
                  className="!w-full !py-2 !px-4 !rounded-lg"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg font-bold hover:bg-gray-500 transition-colors" style={{ color: 'var(--text-color)' }}>ยกเลิก</button>
                <button
                  onClick={handleSubmitAll}
                  disabled={transactions.length === 0 || isSubmitting}
                  className={`px-8 py-3 rounded-lg font-black shadow-xl transition-transform flex items-center justify-center gap-2 bg-[var(--button-edit-bg)] text-[var(--text-black)] ${transactions.length > 0 && !isSubmitting ? 'active:scale-95' : 'opacity-50 cursor-not-allowed'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  ยืนยันทำรายการ ({transactions.length})
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================== 📱 MOBILE VIEW ============================== */}
        <div className="flex md:hidden flex-col w-full h-full bg-[var(--component-bg-color)]">
          <div className="p-4 border-b flex justify-between items-center bg-black/10 shrink-0 border-[var(--border-item-light)]">
            <h2 className="text-xl font-bold">ซื้อขายเหรียญ</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-500 transition-colors" aria-label="ปิด">✕</button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar">
            <div className="bg-[var(--bg-item)] p-4 rounded-xl border border-[var(--border-item-light)]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1 opacity-80">พอร์ตหลัก</label>
                  {portfolioSelect(`${inputClsMobile} focus:outline-none`)}
                </div>
                {subPorts.length > 0 && (
                  <div>
                    <label className="block text-xs font-bold mb-1 opacity-80">พอร์ตย่อย</label>
                    {subPortSelect(`${inputClsMobile} focus:outline-none`)}
                  </div>
                )}
              </div>
              {selectedPortId && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3 border-[var(--border-item-light)]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold opacity-80">เงินสด:</span>
                    <span className="text-sm font-bold text-green-500" title={formatCryptoPrice(cashExact)}>${shortenDecimalText(formatCryptoPrice(cashExact))}</span>
                  </div>
                  <button onClick={() => { setCashTopUp(undefined); setIsManageCashOpen(true); }} className="text-[10px] font-bold px-2 py-1 rounded bg-yellow-400/20 text-yellow-500 uppercase">ปรับยอด</button>
                </div>
              )}
            </div>

            <div className={`bg-[var(--bg-item)] p-4 rounded-xl border transition-all duration-300 border-[var(--border-item-light)] ${isAnimating ? 'ring-2 ring-yellow-400 scale-[1.01] shadow-lg' : ''}`}>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-bold mb-1 opacity-80">ประเภทรายการ</label>
                  <select value={type} onChange={(e) => setType(e.target.value as TradeType)} className={`${inputClsMobile} font-black`}>
                    <option value="BUY">BUY (ซื้อ)</option>
                    <option value="SELL">SELL (ขาย)</option>
                  </select>
                </div>

                <div className="col-span-2 relative">
                  <label className="block text-xs font-bold mb-1 opacity-80">เหรียญ</label>
                  <input
                    type="text"
                    value={symbolInput}
                    onChange={handleSearchChange}
                    onFocus={() => { if (symbolInput && !asset) setShowDropdown(true); }}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    placeholder="BTC"
                    autoComplete="off"
                    className={`${inputClsMobile} uppercase font-black`}
                  />
                  {symbolDropdown}
                </div>

                {filteredHoldings.length > 0 && (
                  <div className="col-span-2 mt-1 mb-2">
                    <div className="text-[10px] font-bold opacity-60 mb-2">แตะเพื่อจัดการเหรียญในพอร์ต:</div>
                    <div className="flex overflow-x-auto gap-2 pb-2 no-scrollbar">
                      {filteredHoldings.map((h: any) => (
                        <div key={h.assetId || h.stockSymbol} className="shrink-0 flex items-center border rounded-lg overflow-hidden border-[var(--border-item-light)] bg-[var(--button-bg-color)] shadow-sm">
                          <div className="px-3 py-2 flex flex-col border-r border-[var(--border-item-light)]">
                            <span className="text-xs font-black text-[var(--text-color)]">
                              {h.displaySymbol || h.assetSymbol} <span className="opacity-60 ml-1 font-medium" title={formatCryptoQty(h.quantity)}>@{shortenDecimalText(formatCryptoQty(h.quantity))}</span>
                            </span>
                            <span className="text-[10px] font-semibold" style={{ color: 'var(--yellow-text-color)' }} title={formatCryptoPrice(h.avgEntryPrice ?? h.avgBuyPrice ?? '0')}>
                              ${shortenDecimalText(formatCryptoPrice(h.avgEntryPrice ?? h.avgBuyPrice ?? '0'))}
                            </span>
                          </div>
                          <button onClick={() => handleActionClick(h, 'BUY')} className="px-3 py-2 text-xs font-bold text-green-500 hover:bg-green-500/25 transition-colors">ซื้อ</button>
                          <button onClick={() => handleActionClick(h, 'SELL')} className="px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-500/25 transition-colors">ขาย</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="col-span-2 border-t pt-3 border-[var(--border-item-light)]"></div>

                <div className="col-span-2">
                  <label className="block text-xs font-bold mb-1 opacity-80">วันที่ (Date)</label>
                  <input type="date" value={date} max={getTodayStr()} onChange={(e) => setDate(e.target.value)} className={inputClsMobile} />
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-bold mb-1 opacity-80">จำนวน (Qty)</label>
                  <input {...decimalProps(quantity, setQuantity)} className={inputClsMobile} />
                  {sellAllButton(true)}
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-bold mb-1 opacity-80">ราคา USD (Price)</label>
                  <input {...decimalProps(price, setPrice)} className={inputClsMobile} />
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-bold mb-1 opacity-80">ธรรมเนียม (Fee)</label>
                  <input {...decimalProps(fee, setFee)} className={inputClsMobile} />
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-bold mb-1 opacity-80">ภาษี (Tax)</label>
                  <input {...decimalProps(tax, setTax)} className={inputClsMobile} />
                </div>

                <div className="col-span-2 mt-3">
                  <button onClick={handleAddTransaction} className="w-full py-3 rounded-lg font-black shadow-md transition-transform active:scale-95 flex items-center justify-center gap-2" style={{ color: '#000', backgroundImage: 'linear-gradient(to right, #facc15, #eab308)' }}>
                    เพิ่มลงคิวทำรายการ
                  </button>
                </div>
                {guardBanner}
              </div>
            </div>

            {transactions.length > 0 && (
              <div>
                <h3 className="font-bold mb-3 text-sm">รายการรอยืนยัน ({transactions.length})</h3>
                <div className="space-y-2">
                  {transactions.map(t => {
                    const { gross, net } = queueRowNet(t);
                    const err = rowErrors[t.id];
                    return (
                      <div key={t.id} className={`p-3 rounded-lg border flex flex-col gap-2 border-[var(--border-item-light)] ${err ? 'bg-red-500/10 border-red-500/40' : t.isNewAsset ? 'bg-purple-500/20' : 'bg-[var(--bg-item)]'}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-black" style={{ color: t.type === 'BUY' ? 'var(--green-btn)' : 'var(--red-text-color)' }}>{t.type}</span>
                              <span className="font-bold text-[var(--text-color)]">{t.asset.displaySymbol}</span>
                            </div>
                            <div className="text-[11px] text-gray-400 font-medium">
                              📅 {new Date(t.date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                            <span className="text-[10px] font-normal text-yellow-400/80 truncate max-w-[200px] block">{selectedPortfolio?.name} / {t.subPortName}</span>
                          </div>
                          <div className="flex gap-1.5">
                            <button onClick={() => handleEditTransaction(t)} className="text-yellow-500 text-xs font-bold bg-yellow-500/10 px-2 py-1 rounded">แก้ไข</button>
                            <button onClick={() => handleRemoveTransaction(t.id)} className="text-red-500 text-xs font-bold bg-red-500/10 px-2 py-1 rounded">ลบ</button>
                          </div>
                        </div>
                        <div className="flex justify-between gap-2 text-xs opacity-80 mt-1">
                          <span className="break-all">จำนวน: {formatCryptoQty(t.quantity)} @ ${formatCryptoPrice(t.price)}</span>
                          <span className="whitespace-nowrap">มูลค่า: ${formatCryptoUsd(gross)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs mt-1 border-t border-gray-500/20 pt-2">
                          <span className="opacity-60">F: ${formatCryptoPrice(t.fee)} | T: ${formatCryptoPrice(t.tax)}</span>
                          <span className="font-black text-[var(--yellow-text-color)] text-sm">Net: ${formatCryptoUsd(net)}</span>
                        </div>
                        {err && <div className="text-xs font-bold text-red-400">⚠ {err}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t flex flex-col justify-end gap-3 bg-black/10 shrink-0 border-[var(--border-item-light)]">
            <button
              onClick={handleSubmitAll}
              disabled={transactions.length === 0 || isSubmitting}
              className={`w-full py-3 rounded-lg font-black shadow-xl transition-transform flex items-center justify-center gap-2 bg-[var(--button-edit-bg)] text-[var(--text-black)] ${transactions.length > 0 && !isSubmitting ? 'active:scale-95' : 'opacity-50'}`}
            >
              ยืนยันทำรายการทั้งหมด ({transactions.length})
            </button>
            <button onClick={onClose} className="w-full py-3 rounded-lg font-bold bg-[var(--button-bg-color)] text-[var(--text-color)]">ยกเลิก</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoTradeModal;
